import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Cookie, Megaphone, ShieldCheck } from "lucide-react";

const KEY = "juris_cookie_consent";

const readConsent = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveConsent = (value) => {
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ ...value, ts: new Date().toISOString() })
    );
  } catch {
    /* Privat tilstand: banneret vises da igen ved næste besøg */
  }
};

const CHECKBOX_CLASS =
  "mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-600";

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [stats, setStats] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!readConsent()) setOpen(true);
    const reopen = () => {
      const c = readConsent();
      setStats(Boolean(c?.statistics));
      setMarketing(Boolean(c?.marketing));
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener("juris:cookie-settings", reopen);
    return () => window.removeEventListener("juris:cookie-settings", reopen);
  }, []);

  const choose = (statistics, marketing) => {
    saveConsent({ necessary: true, statistics, marketing });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Dit cookievalg"
      className="fixed inset-x-4 bottom-24 z-[60] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-slate-900/15 sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-md"
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Cookie className="h-4 w-4 text-brand-600" />
        Vi bruger cookies
      </p>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        Vi sætter kun teknisk nødvendige cookies som standard. Statistik-cookies
        skal du selv vælge ind for — og dit valg gælder i 12 måneder. Læs mere i{" "}
        <Link
          to="/cookiepolitik"
          data-testid="cookie-policy-link"
          className="font-medium text-brand-600 hover:underline"
        >
          cookiepolitikken
        </Link>
        .
      </p>

      {details && (
        <div data-testid="cookie-details" className="mt-4 space-y-2">
          <div className="flex items-start gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">Nødvendige</p>
              <p className="text-xs leading-relaxed text-slate-500">
                Gør siden velfungerende og husker dit cookievalg — altid aktive.
              </p>
            </div>
            <span className="text-xs font-medium text-emerald-600">Altid</span>
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 px-3 py-2.5 transition-colors duration-200 hover:bg-slate-50">
            <input
              type="checkbox"
              data-testid="cookie-toggle-statistics"
              checked={stats}
              onChange={(e) => setStats(e.target.checked)}
              className={CHECKBOX_CLASS}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">Statistik</p>
              <p className="text-xs leading-relaxed text-slate-500">
                Anonym brugsstatistik, der hjælper os med at forbedre siden.
              </p>
            </div>
            <BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 px-3 py-2.5 transition-colors duration-200 hover:bg-slate-50">
            <input
              type="checkbox"
              data-testid="cookie-toggle-marketing"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className={CHECKBOX_CLASS}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">Marketing</p>
              <p className="text-xs leading-relaxed text-slate-500">
                Bruges ikke i dag — kategorien er klar, hvis det ændrer sig.
              </p>
            </div>
            <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
          </label>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          data-testid="cookie-accept-all"
          onClick={() => choose(true, true)}
          className="flex-1 rounded-full bg-brand-950 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-950/25 transition-all duration-200 hover:bg-brand-900"
        >
          Accepter alle
        </button>
        <button
          type="button"
          data-testid="cookie-necessary-only"
          onClick={() => choose(false, false)}
          className="flex-1 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-slate-300 hover:text-slate-900"
        >
          Kun nødvendige
        </button>
      </div>
      {details ? (
        <button
          type="button"
          data-testid="cookie-accept-selected"
          onClick={() => choose(stats, marketing)}
          className="mt-3 w-full rounded-full border border-brand-950/20 bg-brand-50/60 px-5 py-2.5 text-sm font-semibold text-brand-950 transition-colors duration-200 hover:bg-brand-50"
        >
          Gem mine valg
        </button>
      ) : (
        <button
          type="button"
          data-testid="cookie-details-toggle"
          onClick={() => setDetails(true)}
          className="mt-3 text-sm font-medium text-slate-500 transition-colors hover:text-brand-600"
        >
          Vis detaljer og vælg selv
        </button>
      )}
    </div>
  );
};

export default CookieConsent;
